import React, {createContext, useCallback, useEffect, useMemo, useState} from 'react';
import {LavoratorePresenzeRecord} from '@commons/mapping/LavoratorePresenzeRecord';
import {GestionePlus} from '@commons/services/presenze_calcolo/GestionePlus';
import {GestionePlusCommonCtrl} from '@commons/services/presenze_calcolo/GestionePlusCommonCtrl';
import {FPNetDate, IndiceMese, Periodo} from '@commons/types';
import {AziendaRecord} from '@commons/mapping/AziendaRecord';
import {useImmer} from 'use-immer';
import {Draft} from "immer";
import {cloneDeep} from 'lodash';
import {PresenzeGiornataPlusRecord} from '@commons/mapping/PresenzeGiornataPlusRecord';
import {StatoWeekPLUSRecord} from '@commons/mapping/StatoWeekPLUSRecord';
import {RiepilogoOrePlus} from '@commons/mapping/RiepilogoOrePlus';
import {UserCRUDRecord} from '@commons/mapping/UserCRUDRecord';
import {useAppSelector} from '@hooks/redux-hooks/hooks';
import {selectUser} from '@commons/core.slice';
import {useCounter} from 'usehooks-ts';
import {invalidateCache} from "@commons/services/presenze_calcolo/Loader";
import {CalendarioChiusureRecord} from "@commons/mapping/CalendarioChiusureRecord";
import {CachePresenze} from "@commons/mapping/CachePresenze";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import {TerminaleRecord} from "@commons/mapping/TerminaleRecord";
import {TurnoRecord} from "@commons/mapping/TurnoRecord";
import {DateTime} from "luxon";
import {ConsolidatedData, GestionePlusData} from "@commons/services/presenze_calcolo/GestionePlusData";
import {Calcolo} from "@commons/services/presenze_calcolo/Calcolo";
import {CalcoloPresenzeTask} from "@commons/services/presenze_calcolo/sub_calcolo/CalcoloPresenzeTask";
import {GiustificazioneDiMassaTask} from "@commons/services/presenze_calcolo/sub_calcolo/GiustificazioneDiMassaTask";
import {sort} from "json-keys-sort";

const enableWarn = false;
export type UpdaterRetVal = { lavoratore: LavoratorePresenzeRecord | null | undefined, cache?: CachePresenze } | null;
export type UpdaterLavoratore = (lavoratore: LavoratorePresenzeRecord) => UpdaterRetVal;

export interface UsePresenzeOptions {
    selFirst: boolean,
    employeeOrder: boolean,
    isPaginaPlus: boolean,
    isPaginaWF: boolean,
    isPaginaPianificazione: boolean,
    caricaRichiesteWF: boolean,
    isLoadingInitialState: boolean,
    onLoadStart: () => void,
    onLoadComplete: (lavoratori: LavoratorePresenzeRecord[], idLavoratore: number | null) => void, //callback al completamento del caricamento dei lavoratori e delle presenze del lavoratore selezionato
    loadUL: boolean,
    lavoratori?: LavoratorePresenzeRecord[],
    user?: UserCRUDRecord | null,
}

export const defaultOpt: UsePresenzeOptions = {
    selFirst: true,
    employeeOrder: false,
    isPaginaPlus: false,
    isPaginaWF: false,
    isPaginaPianificazione: false,
    caricaRichiesteWF: false,
    isLoadingInitialState: true,
    onLoadStart: () => {
    },
    onLoadComplete: (_lavoratori: LavoratorePresenzeRecord[], _idLavoratore: number | null) => {
        //console.log(JSON.stringify(ctrl.getCache()));
    },
    loadUL: false // Le unità locali vengono caricate solo per idAzienda===idAziendaMaster, con true vengono caricate sempre e comunque
}

export type Updater = (draft: Draft<PresenzeGiornataPlusRecord>, // oggetto in aggiornamento (draft di immer)
                       dayAggiornato: PresenzeGiornataPlusRecord | undefined, // eventuale istanza day da utilizzare per aggiornare draft
                       week: StatoWeekPLUSRecord | undefined,
                       lavoratore: LavoratorePresenzeRecord | undefined,
) => void;

export const usePresenze = (azienda: AziendaRecord | undefined, periodo: Periodo, opt: UsePresenzeOptions = defaultOpt,
                            minDate?: FPNetDate,
                            maxDate?: FPNetDate,) => {
    const user: UserCRUDRecord | null = useAppSelector(selectUser); // User corrente
    const {count: ctrlCycle, increment} = useCounter(0);

    const [soloMeseRif, setSoloMeseRif] = useState<boolean>(true);
    // In combinazione con <soloMeseRif>, permette il calcolo ed evita un loop sulla relativa useEffect
    const [calcolaTotali, setCalcolaTotali] = useState<boolean>(false);

    const [context, updateContext] = useImmer<PresenzeContextData>({...defContextValue, setSoloMeseRif});
    const [isModified, setIsModified] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(opt.isLoadingInitialState);
    const [lavoratori, setNextLavoratori] = useImmer<LavoratorePresenzeRecord[] | null>(null);
    const [idLavoratore, setNextIdLavoratore] = useState<number | null>(null);
    const [calendario, setNextCalendario] = useState<CalendarioChiusureRecord | null>(null);
    const [cachePresenze, setNextCachePresenze] = useState<CachePresenze | null>(null);
    const [currentDay, setNextCurrentDay] = useState<string | null>(null);

    const isCacheReady = cachePresenze !== null;
    /**
     * Istanza di GestionePlus
     * Viene creata una sola volta o al variare di ctrlCycle ovvero al refresh
     */
    const gestione = useMemo(() => {
        enableWarn && console.warn('usePresenze.ctrlCycle', ctrlCycle); // Solo per evitare ESLint warning...
        setNextLavoratori([]);
        setNextIdLavoratore(null);
        return new GestionePlus({
            isPaginaPlus: opt.isPaginaPlus,
            isPaginaWF: opt.isPaginaWF,
            isPaginaPianificazione: opt.isPaginaPianificazione,
            caricaRichiesteWF: opt.caricaRichiesteWF
        });
    }, [ctrlCycle, setNextLavoratori, opt.isPaginaPlus, opt.isPaginaWF, opt.isPaginaPianificazione, opt.caricaRichiesteWF]);

    const updLocalCache = useCallback((cache: CachePresenze | undefined) => {
        if (cache) {
            const localCache = cloneDeep(cache);
            updateContext((context) => {
                context.cachePresenze = localCache;
                context.listaGiustificativi = localCache ? localCache.elencoGiustificativi : [];
                context.listaTerminali = localCache ? localCache.terminali : [];
                context.listaTurni = localCache ? localCache.turni : [];
                context.listaUnitaLocali = localCache ? localCache.elencoUnitaLocali : [];
            })
            setNextCachePresenze(localCache);
        }
    }, [updateContext]);

    /**
     * Istanza di GestionePlusCommonCtrl
     * Al variare di azienda, gestione
     */
    const ctrl = useMemo(() => {
        enableWarn && console.warn('setup ctrl inizio')
        setIsLoading(true);
        setNextCachePresenze(null)
        setNextLavoratori([]);
        setNextIdLavoratore(null);
        if (azienda && gestione) {
            gestione.idAziendaMaster = azienda.idAziendaMaster;
            gestione.idAzienda = azienda.idAzienda;

            /*gestione.postConsolidaLavoratore = (_p0?: any, _p1?: any, lav?: LavoratorePresenzeRecord) => new Promise<void>((resolve, _reject) => {
                if (lav) {
                    // con ricarica a true, se lav è null allora c'è stato un problema!
                    updLavoratore(lav);
                    const proprietaGestione = LavoratorePresenzeRecord.getProprieta(lav, Periodo.getFineMese(periodo));
                    enableWarn && console.warn('proprietaGestione', proprietaGestione)
                    //enableWarn && console.warn('cachePresenze', cachePresenze)
                    // Fisso calendario chiusure!
                    if (proprietaGestione && cachePresenze) {
                        const calendario = GestionePlus.getCalendario(cachePresenze, opt.isPaginaWF, periodo.anno, proprietaGestione);
                        enableWarn && console.warn('setNextCalendario', calendario)
                        setNextCalendario(calendario);
                    }
                }
                setNextIdLavoratore(lav?.id || null);
                resolve();
            })*/

            const ctrl = GestionePlusCommonCtrl(
                gestione,
                azienda.idDominio!,
                azienda.idAziendaMaster,
                azienda.idAzienda,
                azienda.idAziendaCDIC,
                azienda.idTabellaWF, opt.lavoratori);
            ctrl.loadUnitaLocali(azienda, opt.loadUL).then((elencoUnitaLocali: AziendaRecord[]) => {
                ctrl.initCache().then((globalCache) => {
                    enableWarn && console.warn('fine effettivo setup ctrl, updLocalCache', globalCache)
                    globalCache.elencoUnitaLocali = elencoUnitaLocali;
                    updLocalCache(globalCache);
                })
            });
            enableWarn && console.warn('setup ctrl fine')
            return ctrl;
        } else {
            return null;
        }
    }, [setNextLavoratori, azienda, gestione, opt, updLocalCache]);

    const saveGestioneInLavoratore = useCallback((lavoratore: LavoratorePresenzeRecord): UpdaterRetVal => {
        if (gestione) {
            return gestione.saveGestioneInLavoratore(lavoratore);
        } else {
            return null;
        }
    }, [gestione]);

    const updLavoratore = useCallback((lav: LavoratorePresenzeRecord | null, updFn: UpdaterLavoratore = saveGestioneInLavoratore) => {
        setNextLavoratori((lavoratoriPeriodo) => {
            const lavoratore = lavoratoriPeriodo!.find((lavoratore) => lavoratore.id === lav!.id);
            if (lavoratore) {
                const r = updFn(lavoratore)!;
                if (r && r.cache)
                    updLocalCache(r.cache);
            }
        });

    }, [updLocalCache, setNextLavoratori, saveGestioneInLavoratore]);

    const caricaLavoratori = useCallback((idDip: number | undefined, periodo: Periodo) => {
        if (azienda && gestione && ctrl) {
            //enableWarn && console.warn('caricaLavoratori');
            opt.onLoadStart();
            setNextLavoratori([]);
            setNextIdLavoratore(null);
            setNextCalendario(null);
            ctrl.loadTabelleAnno(azienda, periodo.anno, 'presenze').then(({calendarioChiusure}) => {
                setNextCalendario(calendarioChiusure);
                //ctrl.loadLavoratori(idDip, opt.selFirst, true, opt.employeeOrder, user).then(payload => {
                ctrl.loadLavoratori(idDip, true, true, opt.employeeOrder, user).then(payload => {
                    const {lavoratori, lavoratore} = payload;
                    if (lavoratori) {
                        const nextLavoratori = cloneDeep(lavoratori);
                        setNextLavoratori(nextLavoratori);//.filter(lav => lav.id === 52541)
                        setTimeout(() => {
                            const idLavoratore = lavoratore ? lavoratore.id : null;
                            const nextLavoratore = idLavoratore ? nextLavoratori.find((lav: LavoratorePresenzeRecord) => lav.id === idLavoratore) : null;
                            if (nextLavoratore) {
                                updLavoratore(nextLavoratore);
                                setNextIdLavoratore(idLavoratore);
                            }
                            opt.onLoadComplete(nextLavoratori, nextLavoratore?.id || null);
                        }, 0);
                    } else {
                        opt.onLoadComplete([], null);
                    }
                    setIsLoading(false);
                }, (err) => {
                    console.error(err);
                });
            });
        }
    }, [azienda, gestione, ctrl, opt, setNextLavoratori, user, updLavoratore]);

    //useWhatChanged([caricaLavoratori],'caricaLavoratori')
    useEffect(() => {
        if (gestione && isCacheReady) {
            gestione.setPeriodo(periodo.anno, periodo.mese);
            caricaLavoratori(periodo.idDip, periodo);
        }
    }, [periodo, gestione, isCacheReady, caricaLavoratori]);

    /*    useEffect(() => {
            gestione.setPeriodo(periodo.anno, periodo.mese);
            caricaLavoratori();
        }, [caricaLavoratori, gestione, periodo.anno, periodo.mese]);*/

    const reloadAll = () => {
        increment();
        invalidateCache(); // Invalida tutte le caches!
    }

    const setDayModified = useCallback((day2Update: PresenzeGiornataPlusRecord, modified: boolean) => {
        setIsModified(modified);
        gestione.setModified(day2Update);
    }, [setIsModified, gestione]);

    // Helper per l'aggiornamento attraverso il costrutto di immer dei giorni indicati da <days>:
    // per ciascuno di questi giorni viene chiamata la funzione indicata in callback <updater>.
    // <updater> riceve:
    //      day: PresenzeGiornataPlusRecord,
    //      week: StatoWeekPLUSRecord | undefined,
    //      lavoratore: LavoratorePresenzeRecord
    const updateDays = useCallback((days: PresenzeGiornataPlusRecord[], updater: Updater, setModified: boolean = false) => {
        for (const dayModificato of days) {
            setNextLavoratori((lavoratori) => {
                const lavoratore = lavoratori!.find((lavoratore) => lavoratore.id === dayModificato.idDip);
                if (lavoratore && lavoratore.days) {
                    const day2Update = lavoratore.days!.find(day => day.dtGiornata === dayModificato.dtGiornata);
                    if (day2Update) {
                        if (setModified)
                            setDayModified(day2Update, true);
                        const week2Update = lavoratore.weeks!.find(week => StatoWeekPLUSRecord.contains(week, dayModificato))
                        updater(day2Update, dayModificato, week2Update, lavoratore);
                    }
                }
            });
        }
    }, [setNextLavoratori, setDayModified]);

    const replaceDays = useCallback((days: PresenzeGiornataPlusRecord[], calcola: boolean = false) => {
        for (const dayModificato of days) {
            setNextLavoratori((lavoratori) => {
                const lavoratore = lavoratori!.find((lavoratore) => lavoratore.id === dayModificato.idDip);
                if (lavoratore) {
                    const dayIndex2Update = lavoratore.days!.findIndex(day => day.dtGiornata === dayModificato.dtGiornata);
                    if (dayIndex2Update >= 0) {
                        lavoratore.days![dayIndex2Update] = PresenzeGiornataPlusRecord.factory({...dayModificato, backup: cloneDeep(dayModificato)});
                        if (calcola) {
                            PresenzeGiornataPlusRecord.resetAnomalia(lavoratore.days![dayIndex2Update]);
                            const success = RiepilogoOrePlus.calcolaConESenzaTimbrature(lavoratore.days![dayIndex2Update].totali!, lavoratore.days![dayIndex2Update].causaliHH, gestione.getListaGiustificativi());
                            if (!success) {
                                PresenzeGiornataPlusRecord.setAnomalia(lavoratore.days![dayIndex2Update], 4);
                            } else {
                                lavoratore.days![dayIndex2Update].anomalia = 0;
                            }
                        }
                    }
                }
            });
        }
    }, [gestione, setNextLavoratori]);

    const rollback = (idDip: number) => {
        const lavoratore = lavoratori?.find(lav => lav.id === idDip);
        if (lavoratore && lavoratore.days) {
            const days: PresenzeGiornataPlusRecord[] = [];
            for (const day of lavoratore.days) {
                if (day.modified && day.backup) {
                    days.push(day.backup);
                }
            }
            if (days.length > 0)
                replaceDays(days!);
        }
    }

    const lavoratore = useMemo(() => {
        return lavoratori && idLavoratore ? lavoratori.find((lav) => lav.id === idLavoratore) : undefined;
    }, [lavoratori, idLavoratore]);

    useEffect(() => {
        setNextCurrentDay(null); // Al variare del lavoratore, resetto giorno corrente
    }, [idLavoratore]);

    useEffect(() => {
        enableWarn && console.log('lavoratore', lavoratore?.id)
        if (lavoratore) {
            const proprietaGestione = LavoratorePresenzeRecord.getProprieta(lavoratore, Periodo.getFineMese(periodo));
            enableWarn && console.log('proprietaGestione', proprietaGestione)

            // Fisso calendario chiusure!!!
            if (proprietaGestione && cachePresenze) {
                const calendario = GestionePlus.getCalendario(cachePresenze, opt.isPaginaWF, periodo.anno, proprietaGestione);
                enableWarn && console.log('setNextCalendario', calendario)
                setNextCalendario(calendario);
            } else {
                setNextCalendario(null);
            }
        }
    }, [cachePresenze, lavoratore, opt.isPaginaWF, periodo]);

    /*const calcolaPresenze = useMemo(() => {
        const isEmployee = user?.userClass === 'EMPLOYEE';
        return cachePresenze ? (isEmployee && opt.isPaginaWF && !!cachePresenze.tabellaWF?.calcolaPresenze) : false;
    }, [user, cachePresenze, opt.isPaginaWF]);*/
    const calcolaPresenze = useMemo(() => {
        return ctrl?.hasCalcolaPresenze(user, cachePresenze);
    }, [cachePresenze, ctrl, user]);

    // Alla conferma del lavoratore
    const selectLavoratore = useCallback(
        (idLavoratore: number,
         onSuccess: () => void = () => {
         },
         onError: () => void = () => {
         }, ricarica: boolean = false) => {
            if (!lavoratore || idLavoratore !== lavoratore.id || !lavoratore.loaded || ricarica) {
                if (ctrl) {
                    //enableWarn && console.warn('selectLavoratore',idLavoratore);
                    //dispatch(setupBusy(true));
                    setIsLoading(true);
                    ctrl.setLavoratoreById(idLavoratore, true, minDate, maxDate, calcolaPresenze).then((lav) => {
                        enableWarn && console.warn('setLavoratoreById, proprieta', lav?.proprieta)
                        if (lav) {
                            // con ricarica a true, se lav è null allora c'è stato un problema!
                            updLavoratore(lav);
                        }
                        setNextIdLavoratore(lav?.id || null);

                        //dispatch(setupBusy(false));
                        setIsLoading(false);
                        onSuccess();
                    }, () => {
                        setIsLoading(false);
                        onError();
                    });
                }
            } else onSuccess();
        },
        [calcolaPresenze, lavoratore, ctrl, minDate, maxDate, updLavoratore]
    );

    const getLavoratoreNext = useCallback((): LavoratorePresenzeRecord | null => {
        return !ctrl ? null : ctrl.getLavoratoreNext();
    }, [ctrl]);
    const getLavoratorePrev = useCallback((): LavoratorePresenzeRecord | null => {
        return !ctrl ? null : ctrl.getLavoratorePrev();
    }, [ctrl]);

    const hasDaysToSave = useCallback((lavoratore: LavoratorePresenzeRecord) => {
        let daysToSave = false;
        /*
                if (!lavoratore)
                    lavoratore = (lavoratori && idLavoratore ? lavoratori.find((lav) => lav.id === idLavoratore) : undefined);
        */
        if (lavoratore && lavoratore.days) {
            for (const day of lavoratore.days) {
                if (day.modified) {
                    daysToSave = true;
                    break;
                }
            }
        }
        return daysToSave;
    }, []);

    useEffect(() => {
        if (lavoratore && context.consolidatedData) {
            const calcolo = Calcolo.getInstance(cachePresenze!, lavoratore, periodo, soloMeseRif, true, context.consolidatedData);
            updateContext((context) => {
                context.calcolaGiornata = (day: PresenzeGiornataPlusRecord, soloMeseRif: boolean): GestionePlusData | null => {
                    enableWarn && console.log('Calcola giornata');
                    calcolo.setSoloMeseRif(soloMeseRif);
                    calcolo.calcolaGiornata({
                        day2calculate: day,
                        calcolaEffettive: day.arrotondateLocked,
                    });
                    return calcolo.getGestione();
                }
                //day.totali = merge(day.totali, {hOrdDiurne: 10});

                context.compensaGiornata = (day: PresenzeGiornataPlusRecord, soloMeseRif: boolean) => {
                    enableWarn && console.log('compensaGiornata')
                    calcolo.setSoloMeseRif(soloMeseRif);
                    calcolo.calcolaGiornata({
                        day2calculate: day,
                        calcolaEffettive: false,
                        compensa: true,
                    });
                    return calcolo.getGestione();
                }
            });
        }

    }, [cachePresenze, context.consolidatedData, lavoratore, periodo, soloMeseRif, updateContext, opt.isPaginaWF]);

    const selectGiornata = useCallback((day: PresenzeGiornataPlusRecord | null, aggiornaSoloContext: boolean = false) => {
        if (day) {
            if (!aggiornaSoloContext)
                setNextCurrentDay(day.dtGiornata);
            //gestione.consolidaGiornata(day, false, false);

            updateContext((context) => {
                context.consolidatedData = GestionePlusData.consolidaGiornata(periodo, lavoratore!, day, cachePresenze!, opt.isPaginaWF, true).consolidatedData;
            })
        } else
            setNextCurrentDay(null);
    }, [periodo, lavoratore, cachePresenze, opt, updateContext]);


    useEffect(() => {
        updateContext((context) => {
            context.idAzienda = azienda?.idAzienda || 0;
            context.idAziendaMaster = azienda?.idAziendaMaster || 0;
        })
    }, [updateContext, azienda]);

    useEffect(() => {
        updateContext((context) => {
            context.periodo = periodo;
        })
    }, [updateContext, periodo]);

    useEffect(() => {
        updateContext((context) => {
            context.calendarioChiusure = calendario;
        })
    }, [updateContext, calendario]);

    useEffect(() => {
        updateContext((context) => {
            context.currentDay = currentDay;
        })
    }, [updateContext, currentDay]);

    useEffect(() => {
        updateContext((context) => {
            context.selectGiornata = selectGiornata;
        })
    }, [updateContext, selectGiornata]);

    // Effetto per aggiornare il flag soloMeseRif sul context. Contestualmente set il flag per permettere di ricalcolare i totali!
    useEffect(() => {
        updateContext((context) => {
            context.soloMeseRif = soloMeseRif;
            setCalcolaTotali(true);
        });
    }, [soloMeseRif, updateContext, setCalcolaTotali]);

    useEffect(() => {
        if (lavoratore && cachePresenze && calcolaTotali) {
            setCalcolaTotali(false);
            updLavoratore(lavoratore, (target: LavoratorePresenzeRecord): UpdaterRetVal => {
                Calcolo.updateTotaliMese(target, target.totaliMese!, soloMeseRif, cachePresenze);
                return {} as UpdaterRetVal;
            });
        }
    }, [calcolaTotali, setCalcolaTotali, lavoratore, soloMeseRif, updLavoratore, cachePresenze]);

    const calcolaPeriodo = (_valutaSeInEdit: boolean = true): void => {
        //alert('Funzione "calcolaPeriodo()" non implementata!');
        enableWarn && console.log('calcolaPeriodo');
        if (lavoratore) {
            let impossibileProcedere: boolean = false;
            //impossibileProcedere = valutaSeInEdit ? isInEdit('Consolidare o annullare le modifiche effettuate prima di procedere...') : false;
            //impossibileProcedere = impossibileProcedere || (valutaSeInEdit ? hasDaysToSave('Salvare le modifiche effettuate prima di procedere...') : false);
            if (!impossibileProcedere) {
                const calcolo = Calcolo.getInstance(cachePresenze!, lavoratore, periodo, soloMeseRif, true);
                const task: CalcoloPresenzeTask = CalcoloPresenzeTask.factory({
                    anno: periodo.anno,
                    mese: periodo.mese,
                    livelloPresenzeChiusura: user!.presenze.chiusura
                });

                const startDate = Periodo.getInizioMese(periodo);
                task.dataIniziale = startDate.toISODate();
                const oggi = DateTime.now();
                if (oggi.year === periodo.anno && oggi.month === periodo.mese) {
                    task.dataFinale = oggi.startOf('day').minus({day: 1}).toISODate(); // ieri
                } else {
                    task.dataFinale = Periodo.getFineMese(periodo).toISODate(); // fine mese
                }

                const results: PresenzeGiornataPlusRecord[] = []
                calcolo.calcolaInteroPeriodo(
                    (day, impostaPrevistoConESenza, giornataBloccataConWF) => {
                        const {consolidatedData, calcolabile} = GestionePlusData.consolidaGiornata(periodo, lavoratore!, day, cachePresenze!, opt.isPaginaWF, true);
                        if (calcolabile) {
                            // Chiamo adeguaEffManuali, utile solo per comparazione json con versione AngularJS
                            day.effettiveManualiFlat = GestionePlus.adeguaEffManuali(day, day.effettiveManualiFlat);

                            calcolo.setSoloMeseRif(soloMeseRif);
                            const oldDay = JSON.stringify(day, PresenzeGiornataPlusRecord.replacer);
                            calcolo.calcolaGiornata({
                                day2calculate: day,
                                calcolaEffettive: day.arrotondateLocked,
                                compensa: false,
                                calcoloMassivo: true,
                                impostaPrevistoConESenza,
                                giornataBloccataConWF
                            }, consolidatedData);
                            const newDay = JSON.stringify(day, PresenzeGiornataPlusRecord.replacer);
                            //if (oldDay !== newDay)
                            {
                                results.push(day);
                            }
                            const obj = JSON.parse(newDay);
                            const newDayOrdered = JSON.stringify(sort(obj));
                            day.note = newDayOrdered;
                        }
                    }, task);
                //day.totali=merge(day.totali,{hOrdDiurne:12});
                if (results.length > 0) {
                    replaceDays(results);
                    setCalcolaTotali(true);
                }
            }
        }
    }

    const applicaGM = (daysForGM: PresenzeGiornataPlusRecord[], payload: GiustificazioneDiMassaTask): void => {
        if (lavoratore && lavoratore.days && daysForGM) {
            const gm = GiustificazioneDiMassaTask.factory({
                ...payload,
                idAzienda: context.idAzienda,
                idAziendaMaster: context.idAziendaMaster,
                anno: periodo.anno,
                mese: periodo.mese,
                livelloPresenzeChiusura: user!.presenze.chiusura,
            });
            const calcolo = Calcolo.getInstance(cachePresenze!, lavoratore, periodo, soloMeseRif, true);
            calcolo.setSelected(daysForGM);
            const results = calcolo.giustificazioneMassa(gm, true, soloMeseRif, true);
            if (results.length > 0) {
                replaceDays(results);
                setCalcolaTotali(true);
            }
            //alert('Metodo da implementare! ' + JSON.stringify(gm));
        }
    };

    return {
        calendarioChiusure: calendario,
        reloadAll,
        ctrl,
        lavoratore,
        selectLavoratore,
        getLavoratoreNext,
        getLavoratorePrev,
        lavoratori,
        isLoading,
        updLavoratore,
        isModified,
        setDayModified,
        updateDays,
        //replaceDays,
        rollback,
        ctrlCycle,
        hasDaysToSave,
        cachePresenze,
        calcolaPeriodo,
        applicaGM,
        listaGiustificativi: context.listaGiustificativi,
        listaTerminali: context.listaTerminali,
        listaTurni: context.listaTurni,
        listaUnitaLocali: context.listaUnitaLocali,
        context,
    };
};

interface PresenzeContextData {
    readonly idAzienda: number,
    readonly idAziendaMaster: number,
    readonly periodo: Periodo,
    readonly cachePresenze: CachePresenze | null,
    readonly listaGiustificativi: GiustificativoRecord[],
    readonly listaTerminali: TerminaleRecord[],
    readonly calendarioChiusure: CalendarioChiusureRecord | null,
    readonly listaTurni: TurnoRecord[],
    readonly listaUnitaLocali: AziendaRecord[],
    readonly currentDay: string | null,
    readonly selectGiornata: (day: PresenzeGiornataPlusRecord | null, aggiornaSoloContext?: boolean) => void,
    readonly calcolaGiornata: (day: PresenzeGiornataPlusRecord, soloMeseRif: boolean) => GestionePlusData | null,
    readonly compensaGiornata: (day: PresenzeGiornataPlusRecord, soloMeseRif: boolean) => GestionePlusData | null,
    readonly consolidatedData: ConsolidatedData | null,
    readonly soloMeseRif: boolean,
    readonly setSoloMeseRif: React.Dispatch<React.SetStateAction<boolean>>,
}

const defContextValue: PresenzeContextData = {
    idAzienda: 0,
    idAziendaMaster: 0,
    periodo: {anno: DateTime.now().year, mese: DateTime.now().month as IndiceMese},
    cachePresenze: null,
    listaGiustificativi: [],
    listaTerminali: [],
    calendarioChiusure: null,
    listaTurni: [],
    listaUnitaLocali: [],
    currentDay: null,
    consolidatedData: null,
    soloMeseRif: true,
    selectGiornata: (_day: PresenzeGiornataPlusRecord | null, _aggiornaSoloContext?: boolean) => {
    },
    calcolaGiornata: (_day: PresenzeGiornataPlusRecord, _soloMeseRif: boolean): GestionePlusData | null => {
        return null;
    },
    compensaGiornata: (_day: PresenzeGiornataPlusRecord, _soloMeseRif: boolean): GestionePlusData | null => {
        return null;
    },
    setSoloMeseRif: (p: boolean | ((prevState: boolean) => boolean)) => p,
}
export const PresenzeContext = createContext<PresenzeContextData>(defContextValue);